import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axiosInstance from '../utils/axios';
import { WSO2_URL, AUTH_URL } from '../config';
import { setUser as setUserRedux, logout as logoutRedux } from '../store/authSlice';
import { disconnectSocket } from '../utils/socket';

interface User {
  id: string;
  username: string;
  fullname: string;
  email: string;
  roles: string[];
  is_active: boolean;
  created_at?: string;
  department?: string;
  department_id?: number | null;
  /** Tên đơn vị từ bảng donvi (theo department_id), do /auth/user bổ sung */
  donvi_name?: string | null;
  sync_account?: number;
  /** 1=phòng ban trong ban, 2=tư vấn/nhà thầu - từ đơn vị (DonVi) của user */
  donvi_type?: number;
  /** 1= thuộc module tư vấn nhà thầu (từ bảng users) */
  tuvan_nhathau?: number;
}

/** Cấu hình đơn vị tư vấn nhà thầu (không chứa totp_secret) */
export interface TuvanNhathauUnitConfig {
  id?: number;
  donvi_id?: number;
  enabled_2fa?: string;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  loading: boolean;
  setToken: (token: string) => void;
  /** Cấu hình đơn vị tư vấn nhà thầu (từ /auth/user), null nếu không có */
  tuvanNhathauUnitConfig: TuvanNhathauUnitConfig | null;
  /** Đã xác thực 2FA trong session (backend session) */
  twoFaVerified: boolean;
  setTwoFaVerified: (v: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  loading: true,
  setToken: () => {},
  tuvanNhathauUnitConfig: null,
  twoFaVerified: false,
  setTwoFaVerified: () => {}
});

export const useAuth = () => useContext(AuthContext);

const STORAGE_KEY_TUVAN_NHATHAU_UNIT_CONFIG = 'tuvan_nhathau_unit_config';

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tuvanNhathauUnitConfig, setTuvanNhathauUnitConfig] = useState<TuvanNhathauUnitConfig | null>(null);
  const [twoFaVerified, setTwoFaVerified] = useState<boolean>(false);
  const isFirstRender = useRef(true);
  const isFetchingRef = useRef(false); // Track xem đang fetch API chưa
  const dispatch = useDispatch();

  /**
   * Helper function để đảm bảo token được lưu vào localStorage
   * Trả về Promise<boolean> để có thể await
   */
  const ensureTokenSaved = async (token: string, maxRetries: number = 5): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Kiểm tra localStorage có available không
        const testKey = '__localStorage_test__';
        localStorage.setItem(testKey, 'test');
        const canWrite = localStorage.getItem(testKey) === 'test';
        localStorage.removeItem(testKey);
        
        if (!canWrite) {
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 100 * attempt));
            continue;
          }
          return false;
        }
        
        // Lưu token
        localStorage.setItem('access_token', token);
        
        // Đợi một chút để localStorage được ghi (đặc biệt quan trọng với Firefox)
        await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        
        // Verify token đã được lưu
        const savedToken = localStorage.getItem('access_token');
        
        if (savedToken === token) {
          return true;
        }
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        }
      }
    }

    return false;
  };

  const setToken = (token: string) => {
    try {
      // Lưu token đồng bộ (cho backward compatibility)
      localStorage.setItem('access_token', token);
    } catch (error) {

    }
  };

  const login = () => {
    try {    
      // Lưu current URL để redirect sau khi login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/sso_callback') {
        const returnUrl = window.location.origin + currentPath;
        sessionStorage.setItem('redirectUrl', returnUrl);
      }
      
      // Redirect đến trang login của API
      const loginUrl = `${AUTH_URL}/login`;
      window.location.replace(loginUrl);
    } catch (error) {

    }
  };

  const logout = () => {
    try {
      // Lưu token trước khi xóa để sử dụng cho API logout
      const accessToken = localStorage.getItem('access_token');
      
      // Gọi API logout từ backend TRƯỚC KHI xóa token
      axiosInstance.post('/auth/logout', {}, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
        .then(response => {
          // Sau khi gọi API thành công, mới xóa dữ liệu local
          setUser(null);
          setIsAuthenticated(false);
          setTuvanNhathauUnitConfig(null);
          setTwoFaVerified(false);
          dispatch(logoutRedux());
          setLoading(false);
          disconnectSocket();
          // Xóa toàn bộ dữ liệu trong localStorage/sessionStorage khi logout
          localStorage.clear();
          sessionStorage.clear();
          
          // Xóa cache của axios
          if (axiosInstance.defaults.headers.common['Authorization']) {
            delete axiosInstance.defaults.headers.common['Authorization'];
          }
          
          // Kiểm tra xem có signal should_reload không
          if (response.data?.data?.should_reload) {
            // Reload page để đăng xuất hoàn toàn
            window.location.reload();
          } else {
            // Nếu không có signal, redirect về trang chủ như cũ
            window.location.href = '/';
          }
        })
        .catch(error => {

          // Nếu có lỗi, vẫn xóa dữ liệu local và chuyển hướng về trang chủ
          setUser(null);
          setIsAuthenticated(false);
          setTuvanNhathauUnitConfig(null);
          setTwoFaVerified(false);
          dispatch(logoutRedux());
          setLoading(false);
          disconnectSocket();
          // Dù API logout lỗi, vẫn đảm bảo xóa toàn bộ dữ liệu lưu trên trình duyệt
          localStorage.clear();
          sessionStorage.clear();
          
          if (axiosInstance.defaults.headers.common['Authorization']) {
            delete axiosInstance.defaults.headers.common['Authorization'];
          }
          
          window.location.href = '/';
        });

    } catch (error) {

      // Nếu có lỗi vẫn chuyển hướng về trang chủ
      window.location.href = '/';
    }
  };

  useEffect(() => {
    let isSubscribed = true;

    const checkAuth = async () => {
      // Skip check auth khi đang logout hoặc loading = false
      if (!loading) {
        return;
      }

      // Tránh gọi API nhiều lần đồng thời
      if (isFetchingRef.current) {
        return;
      }
      
      const currentPath = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const loginSuccess = urlParams.get('login') === 'success';
      
      // Kiểm tra xem có token trong URL params không (từ SSO callback)
      const tokenFromUrl = urlParams.get('access_token');
      
      if (tokenFromUrl) {
        try {
          // QUAN TRỌNG: Đảm bảo token được lưu vào localStorage trước khi tiếp tục
          const tokenSaved = await ensureTokenSaved(tokenFromUrl);
          
          if (!tokenSaved) {

          }
        } catch (error) {

        }
        
        // Xóa token khỏi URL để bảo mật
        urlParams.delete('access_token');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }

      // Kiểm tra token hiện tại trong localStorage (sau khi có thể đã lấy từ URL)
      const existingToken = localStorage.getItem('access_token');
      
      // QUAN TRỌNG: Nếu có login=success, LUÔN gọi API để lấy token từ backend
      // Không skip first render trong trường hợp này
      if (loginSuccess) {
        isFirstRender.current = false; // Reset để đảm bảo gọi API
      }

      // Kiểm tra token hiện tại trong localStorage (sau khi có thể đã lấy từ URL)
      const existingTokenBeforeCheck = localStorage.getItem('access_token');
      
      // Nếu đang ở trang login và có token, vẫn cần gọi API để verify và set user
      // Chỉ skip nếu đang ở login và KHÔNG có token
      if ((currentPath === '/login' || currentPath.includes('/auth/')) && !tokenFromUrl && !existingTokenBeforeCheck) {
        setLoading(false);
        return;
      }

      // Khai báo userEndpoint ở ngoài try để có thể sử dụng trong catch
      let userEndpoint = '/auth/user';
      
      try {
        // Kiểm tra token hiện tại
        const existingToken = localStorage.getItem('access_token');
        
        // QUY TẮC: 
        // 1. Nếu có login=success → LUÔN gọi API (không skip)
        // 2. Nếu không có token → LUÔN gọi API (không skip)
        // 3. Chỉ skip nếu: có token VÀ không phải login success VÀ là first render
        const shouldCallAPI = loginSuccess || !existingToken || !isFirstRender.current;
        
        if (!shouldCallAPI && isFirstRender.current && existingToken && !loginSuccess) {
          isFirstRender.current = false;
          isFetchingRef.current = true; // Đánh dấu đang fetch
          // QUAN TRỌNG: Giữ loading = true để đợi verification hoàn thành
          // Để PrivateRoute không redirect về login trước khi verification xong
          setLoading(true);
          
          // Gọi API để verify token và lấy user info
          try {
            const verifyResponse = await axiosInstance.get('/auth/user');
            if (verifyResponse?.data) {
              const userData = verifyResponse.data.data?.user || verifyResponse.data;
              setUser(userData);
              setIsAuthenticated(true);
              dispatch(setUserRedux(userData));
              
              // Lưu user data vào localStorage
              localStorage.setItem('currentUser', JSON.stringify(userData));
              if (userData.id) {
                localStorage.setItem('user_id', userData.id.toString());
              }
              
              // Lưu roles nếu có
              if (userData.roles) {
                localStorage.setItem('roles', JSON.stringify(userData.roles));
              }
              
              // Lưu tuvan_nhathau_unit_config và two_fa_verified
              const unitConfig = verifyResponse.data.data?.tuvan_nhathau_unit_config ?? null;
              setTuvanNhathauUnitConfig(unitConfig);
              if (unitConfig) {
                localStorage.setItem(STORAGE_KEY_TUVAN_NHATHAU_UNIT_CONFIG, JSON.stringify(unitConfig));
              } else {
                localStorage.removeItem(STORAGE_KEY_TUVAN_NHATHAU_UNIT_CONFIG);
              }
              setTwoFaVerified(!!verifyResponse.data.data?.two_fa_verified);
            }
          } catch (error: any) {
            // Nếu token không hợp lệ, xóa và sẽ được xử lý bởi checkAuth lần sau
            if (error?.response?.status === 401) {
              localStorage.removeItem('access_token');
              setUser(null);
              setIsAuthenticated(false);
              dispatch(logoutRedux());
            }
          } finally {
            setLoading(false);
            isFetchingRef.current = false; // Reset flag
          }
          return;
        }
        
        // GỌI API để lấy token từ backend
        isFetchingRef.current = true; // Đánh dấu đang fetch
        setLoading(true);
        isFirstRender.current = false;

        // Nếu login=success hoặc không có token, thêm include_token=true để backend trả token
        const shouldIncludeToken = loginSuccess || !existingToken;
        userEndpoint = shouldIncludeToken ? '/auth/user?include_token=true' : '/auth/user';
        
        // QUAN TRỌNG: Verify token vẫn còn trong localStorage trước khi gọi API
        const tokenBeforeApi = localStorage.getItem('access_token');
        if (existingToken && !tokenBeforeApi) {

          setLoading(false);
          return;
        }

        const response = await axiosInstance.get(userEndpoint, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (isSubscribed) {
          if (response.data) {
            // Lưu token vào localStorage nếu có
            const token = response.data.data?.access_token || response.data.access_token;
            
            if (token) {
              try {
                // QUAN TRỌNG: Đảm bảo token từ API được lưu vào localStorage trước khi tiếp tục
                const tokenSaved = await ensureTokenSaved(token);
                
                if (!tokenSaved) {

                }
              } catch (error) {

              }
            }
            
            // Lưu id_token vào sessionStorage nếu có
            if (response.data.data?.id_token) {
              sessionStorage.setItem('id_token', response.data.data.id_token);
            }
            
            // Set user data từ response.data.data.user hoặc response.data
            const userData = response.data.data?.user || response.data;
            
            if (userData) {
              // QUAN TRỌNG: Set state theo thứ tự để đảm bảo consistency
              setUser(userData);
              setIsAuthenticated(true);
              dispatch(setUserRedux(userData));
            }

            // Lưu user data vào localStorage
            localStorage.setItem('currentUser', JSON.stringify(userData));
            localStorage.setItem('user_id', userData.id.toString());

            // Lưu roles vào localStorage nếu có
            if (response.data.data?.user?.roles) {
              localStorage.setItem('roles', JSON.stringify(response.data.data.user.roles));
            }

            // Xóa pmis_users khỏi localStorage để load lại dữ liệu mới khi login
            localStorage.removeItem('pmis_users');

            // Lưu incoming_docs_detail và outgoing_docs_detail vào localStorage nếu có
            if (response.data.data?.settings?.incoming_document_display) {
              localStorage.setItem('incoming_document_display', JSON.stringify(response.data.data.settings.incoming_document_display));
            }
            if (response.data.data?.settings?.outgoing_document_display) {
              localStorage.setItem('outgoing_document_display', JSON.stringify(response.data.data.settings.outgoing_document_display));
            }
            if (response.data.data?.settings?.incoming_document_default_status) {
              localStorage.setItem('incoming_document_default_status', JSON.stringify(response.data.data.settings.incoming_document_default_status));
            }
            if (response.data.data?.settings?.outgoing_document_default_status) {
              localStorage.setItem('outgoing_document_default_status', JSON.stringify(response.data.data.settings.outgoing_document_default_status));
            }
            // Lưu task_filter_vaitro_default vào localStorage nếu có
            if (response.data.data?.settings?.task_filter_vaitro_default) {
              localStorage.setItem('task_filter_vaitro_default', response.data.data.settings.task_filter_vaitro_default);
            }
            // Lưu tuvan_nhathau_unit_config và two_fa_verified (giống user_settings)
            const unitConfig = response.data.data?.tuvan_nhathau_unit_config ?? null;
            setTuvanNhathauUnitConfig(unitConfig);
            if (unitConfig) {
              localStorage.setItem(STORAGE_KEY_TUVAN_NHATHAU_UNIT_CONFIG, JSON.stringify(unitConfig));
            } else {
              localStorage.removeItem(STORAGE_KEY_TUVAN_NHATHAU_UNIT_CONFIG);
            }
            setTwoFaVerified(!!response.data.data?.two_fa_verified);
            // Nếu là redirect từ login success, xóa param login=success
            if (loginSuccess) {
              const newUrl = window.location.pathname;
              window.history.replaceState({}, '', newUrl);
            }
          } else {
            setUser(null);
            setIsAuthenticated(false);
            dispatch(logoutRedux());
          }
        }
      } catch (error: any) {
        // QUAN TRỌNG: Không xử lý lỗi nếu request bị abort do component unmount
        if (error?.code === 'ECONNABORTED' || error?.message === 'Request aborted') {
          if (isSubscribed) {
            setLoading(false);
          }
          return;
        }
        
        if (isSubscribed) {
          // Nếu là lỗi 401, có thể do chưa login hoặc token không hợp lệ
          if (error?.response?.status === 401) {
            const currentToken = localStorage.getItem('access_token');
            if (currentToken && userEndpoint.includes('include_token=true')) {
              localStorage.removeItem('access_token');
            }
          } else if (error?.response && error.response?.status !== 401) {
            // Chỉ logout nếu có response và không phải 401
            setUser(null);
            setIsAuthenticated(false);
            dispatch(logoutRedux());
          }
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
          isFetchingRef.current = false; // Reset flag
        }
      }
    };

    checkAuth();

    return () => {
      isSubscribed = false;
      isFetchingRef.current = false; // Reset flag khi unmount
    };
  }, [dispatch]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      login,
      logout,
      loading,
      setToken,
      tuvanNhathauUnitConfig,
      twoFaVerified,
      setTwoFaVerified
    }}>
      {children}
    </AuthContext.Provider>
  );
};
